import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Polygon, Tooltip, useMap, CircleMarker } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';

import PollWindow from './PollWindow/PollWindow';
import Fader from '../Loaders/Fader';

import { loadCrimePoints, saveCrimePoints } from '../../../util/LocalDb';
import { getAllCrimePoints } from '../../../http/crimesAPI';

import './Map.scss';

const CACHE_DAYS = 1;
const CACHE_TTL = CACHE_DAYS * 24 * 60 * 60 * 1000; 
const TORONTO_CP = [43.6532, -79.3832]

const CrimeMap = () => {

  const config = useSelector((state) => state.config.config);

  const getBubbleRadius = (count) => {
    if (count >= 20) return 16;
    if (count >= 10) return 12;
    if (count >= 5) return 9;
    return 6;
  };

  const getBubbleColor = (count) => {
    if (count >= 20) return '#d73027';
    if (count >= 10) return '#fc8d59';
    if (count >= 5) return '#fee08b';
    return '#91cf60';
  };

    const getClusterTotalMessages = (cluster) => {
    return cluster.getAllChildMarkers().reduce((sum, marker) => {
      return sum + (marker.options.messageCount || 0);
    }, 0);
  };

  const getClusterSizeByMessages = (count) => {
    if (count >= 500) return 70;
    if (count >= 200) return 60;
    if (count >= 100) return 50;
    if (count >= 50) return 40;
    if (count >= 20) return 32;
    return 26;
  };

  const getClusterColorByMessages = (count) => {
    if (count >= 200) return '#d73027';
    if (count >= 100) return '#fc8d59';
    if (count >= 50) return '#fee08b';
    return '#91cf60';
  };

  const [cp, setCp] = useState([]);
  const [points,setPoints] = useState([])

  const fetchOrLoadData = async () => {
    try {
      const cached = await loadCrimePoints();
      const now = Date.now();
      if (cached && cached.data && cached.timestamp && (now - cached.timestamp < CACHE_TTL)) {
        const data = cached.data
        const cleanData = data.filter(
            p => p && Number.isFinite(p.lat) && Number.isFinite(p.lon)
        );
        setPoints(cleanData);
        if (cleanData.length > 0) {
            setCp([cleanData[0].lat, cleanData[0].lon]);
        } else {
            setCp([43.7, -79.4]);
        }
      } else {
        const res = await getAllCrimePoints(config);
        const data = res?.data || [];
        const cleanData = data.filter(
            p => p && Number.isFinite(p.lat) && Number.isFinite(p.lon)
        );
        setPoints(cleanData);
        if (cleanData.length > 0) {
            setCp([cleanData[0].lat, cleanData[0].lon]);
        } else {
            setCp([43.7, -79.4]);
        }
        await saveCrimePoints({ cleanData, timestamp: now });
      }
    } catch (error) {
      console.error('Error fetching or loading points:', error);
    }
  };


  useEffect(() => {
    if (config) {
      setTimeout(() => {
        fetchOrLoadData();
      }, 1000);
    }
  }, [config]);

    const createClusterCustomIcon = (cluster) => {
      const totalMessages = getClusterTotalMessages(cluster);
      const size = getClusterSizeByMessages(totalMessages);
      const color = getClusterColorByMessages(totalMessages);
  
      return L.divIcon({
        html: `
          <div
            class="custom-cluster-bubble"
            style="
              width: ${size}px;
              height: ${size}px;
              background: ${color};
              font-size: ${size / 2.6}px;
            "
          >
            ${totalMessages}
          </div>
        `,
        className: 'custom-cluster-wrapper',
        iconSize: [size, size],
      });
    };
  


  return (
    <div>
      <div>
        {cp.length === 2 && points.length > 0 ? (
          <div className="polygon-map">
            <MapContainer style={{ width: '100%', height: '100%' }} zoom={13} center={TORONTO_CP}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MarkerClusterGroup
                    chunkedLoading
                    spiderfyOnMaxZoom
                    showCoverageOnHover={false}
                    zoomToBoundsOnClick
                    maxClusterRadius={50}
                    iconCreateFunction={createClusterCustomIcon}
                >
                    {points.map((p) => (
                        <CircleMarker
                            key={p.label}
                            center={[p.lat, p.lon]}
                            radius={getBubbleRadius(p.message_count)}
                            messageCount={p.message_count}
                            pathOptions={{
                                color: '#333',
                                weight: 1,
                                fillColor: getBubbleColor(p.message_count),
                                fillOpacity: 0.85,
                            }}
                        >
                            <Tooltip>
                                {p.offence}
                            </Tooltip>
                        </CircleMarker>
                    ))}
                    </MarkerClusterGroup>
            </MapContainer>
          </div>
        ) : (
          <div className="sentiment-charts">
            <Fader />
          </div>
        )}
      </div>
    </div>
  );
};

export default CrimeMap;
